import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  MessageSquare,
  Cpu,
  ShieldCheck,
  LineChart,
  Sparkles,
  Globe2,
  Boxes,
  KeyRound,
} from "lucide-react";

/* ---------------- ROTATING CONTENT ---------------- */

const PROMO_BANNERS = [
  "Stay connected with verified market experts",
  "Shape your future with Million2Trillion",
  "Share your expertise with global professionals",
  "Upcoming global market expos & events",
  "Learn markets directly from experts",
  "Pick the right tools from the marketplace",
  "Ask experts before you trade",
  "Discover people worth following",
  "Trade using any platform you prefer",
];

const MARKET_PRICES = [
  "SENSEX 72,845 ▲ 0.62%",
  "NIFTY 50 22,145 ▲ 0.58%",
  "BANK NIFTY 46,210 ▼ 0.12%",
  "DOW JONES 38,420 ▲ 0.31%",
  "NASDAQ 15,742 ▼ 0.18%",
  "GOLD (XAUUSD) 2034.20 ▲ 0.42%",
];

const MARKET_NEWS = [
  "Indian markets open higher led by IT stocks",
  "US markets cautious ahead of inflation data",
  "Gold steady as dollar weakens",
  "Oil prices rise on global supply concerns",
  "Asian markets trade mixed in early session",
];

const COMMUNITY_MESSAGES = [
  {
    role: "FX Trader · London",
    color: "text-blue-400",
    message: "USD looks weak ahead of CPI. Watching DXY 104.20 closely.",
  },
  {
    role: "SEBI-Registered Advisor · India",
    color: "text-green-400",
    message: "NIFTY holding above 22,100. IT stocks showing strength.",
  },
  {
    role: "Crypto Analyst · Singapore",
    color: "text-purple-400",
    message: "BTC still range-bound. No confirmed breakout yet.",
  },
  {
    role: "Derivatives Trader · USA",
    color: "text-red-400",
    message: "Volatility expected post-FOMC. Options premiums elevated.",
  },
  {
    role: "Equity Investor · UAE",
    color: "text-yellow-400",
    message: "Selective accumulation phase in quality mid-caps.",
  },
];

/* ---------------- COMPONENT ---------------- */

export default function HeroSection() {
  const [promoIndex, setPromoIndex] = useState(0);
  const [priceIndex, setPriceIndex] = useState(0);
  const [newsIndex, setNewsIndex] = useState(0);
  const [startIndex, setStartIndex] = useState(0);

  /* Promo / prices / news rotation */
  useEffect(() => {
    const timer = setInterval(() => {
      setPromoIndex((p) => (p + 1) % PROMO_BANNERS.length);
      setPriceIndex((p) => (p + 1) % MARKET_PRICES.length);
      setNewsIndex((n) => (n + 1) % MARKET_NEWS.length);
    }, 2600);

    return () => clearInterval(timer);
  }, []);

  /* Community messages rotation (2 visible always) */
  useEffect(() => {
    const interval = setInterval(() => {
      setStartIndex((prev) => (prev + 1) % COMMUNITY_MESSAGES.length);
    }, 3500);

    return () => clearInterval(interval);
  }, []);

  const visibleMessages = [
    COMMUNITY_MESSAGES[startIndex],
    COMMUNITY_MESSAGES[(startIndex + 1) % COMMUNITY_MESSAGES.length],
  ];

  return (
    <section className="flex min-h-screen w-full items-center bg-[#0B0F19] pt-[10px]">
      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-0">
        <div className="mb-24 mt-10 grid grid-cols-1 items-start gap-12 lg:grid-cols-2 lg:px-10">
          {/* ---------------- LEFT ---------------- */}
          <div className="flex flex-col justify-center">
            <h1 className="text-xl font-extrabold leading-tight text-white sm:text-2xl lg:text-3xl">
              The Intelligent, Scam-Free <br />
              <span className="text-[#2563EB]">
                Professional Market Network
              </span>
            </h1>

            <p className="mt-8 max-w-xl text-justify text-xs font-normal text-white sm:text-xs lg:text-sm">
              Connect, chat, trade, learn and teach, post content, reels, and
              videos, sell products and services, build careers and businesses,
              launch products and projects, and access global funding — all in
              one network connecting 200+ verified market roles.
            </p>

            <p className="mt-8 max-w-xl text-justify text-xs font-semibold text-yellow-400 sm:text-xs lg:text-sm">
              “Build your professional identity, grow your network, and unlock
              instant earning potential — alongside millions of active market
              professionals worldwide.”
            </p>

            {/* <iframe
              src="http://localhost:3000/darksignupform"
              title="M2T Dark Signup"
              className="mt-6 h-[200px] w-full max-w-2xl rounded-2xl border border-white/10 bg-white/5"
              sandbox="allow-forms allow-scripts allow-same-origin"
            /> */}

            {/* Launch Hint */}
            <div className="mt-8 text-center text-sm font-normal lg:text-left">
              Launching{" "}
              <span className="relative ml-1 inline-flex items-center px-2 py-0.5">
                <span className="relative z-10 font-medium text-blue-400">
                  soon
                </span>
                <span className="pointer-events-none absolute -inset-1 rounded-full border border-blue-400/50" />
                <span className="pointer-events-none absolute -inset-1 animate-spin rounded-full">
                  <span className="absolute -top-1 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-blue-300 shadow-[0_0_10px_rgba(96,165,250,0.85)]" />
                </span>
              </span>{" "}
              — early registration now open
            </div>

            {/* CTA Buttons */}
            <div className="justify-left mx-auto mt-8 flex flex-col items-center gap-4 rounded-2xl border border-gray-700 bg-white/5 p-4 px-16 sm:flex-row lg:justify-center">
              <Link
                to="/waitlist"
                className="font-semibold text-white/70 underline underline-offset-4 transition hover:text-white"
              >
                Join as Professional →
              </Link>

              <span className="hidden text-white/30 sm:inline">|</span>

              <Link
                to="/partner-signup"
                className="font-semibold text-emerald-600 underline underline-offset-4 transition hover:text-emerald-400"
              >
                Join as Partner →
              </Link>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-6 text-xs font-light text-white">
              <div className="flex items-center gap-2">
                <Boxes size={16} className="text-blue-500/70" />
                <span>Ecosystem</span>
              </div>

              <div className="flex items-center gap-2">
                <Cpu size={16} className="text-blue-500/70" />
                AI-Powered
              </div>

              <div className="flex items-center gap-2">
                <Globe2 size={16} className="text-blue-500/70" />
                Global
              </div>

              <div className="flex items-center gap-2">
                <ShieldCheck size={16} className="text-blue-500/70" />
                KYC Verified
              </div>

              <div className="flex items-center gap-2">
                <KeyRound size={16} className="text-blue-500/70" />
                Encrypted
              </div>
            </div>
          </div>

          {/* ---------------- RIGHT ---------------- */}
          <div className="space-y-5 rounded-2xl border border-white/10 bg-white/5 p-5">
            {/* FEATURED STORY */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="overflow-hidden rounded-xl bg-black/40">
                <img
                  src="/assets/homescreen/global-markets.jpg"
                  alt="Global Market Insight"
                  className="h-full w-full object-cover"
                />
              </div>

              <div className="flex flex-col justify-center">
                {/* <span className="mb-2 text-xs font-semibold uppercase tracking-wide text-blue-400">
                  M2T Market Insight
                </span> */}
                <h2 className="text-base font-semibold text-white/90">
                  How Verified Professionals Are Reshaping Global Market
                  Decisions
                </h2>
                <p className="mt-2 text-xs text-white/70">
                  Verified connections are becoming the new edge in modern
                  markets.
                </p>
                <span className="mt-3 text-xs text-white/40">
                  By M2T Research Desk · Live Beta
                </span>
              </div>
            </div>

            <div className="my-5 h-px w-full bg-white/10" />

            {/* COMMUNITY */}
            <div className="space-y-5">
              {/* AVATARS */}
              <div className="flex items-center justify-between">
                <div className="flex -space-x-2">
                  {[
                    "avatar6",
                    "avatar18",
                    "avatar9",
                    "avatar19",
                    "avatar10",
                    "avatar1",
                    "avatar11",
                  ].map((img, i) => (
                    <img
                      key={i}
                      src={`/assets/avatar/${img}.jpg`}
                      className="h-6 w-6 rounded-full border-2 border-[#0B0F19] object-cover"
                      alt="Verified user"
                    />
                  ))}
                </div>
                <span className="flex items-center gap-2 text-xs text-white/60">
                  <span className="relative flex h-2 w-2">
                    {/* pulse */}
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"></span>
                    {/* solid dot */}
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500"></span>
                  </span>
                  Verified professionals online
                </span>
              </div>

              {/* LIVE FEED (2 MESSAGES) */}
              <div className="space-y-2">
                {visibleMessages.map((item, idx) => (
                  <div
                    key={`${startIndex}-${idx}`}
                    className="animate-fade-slide rounded-xl bg-black/40 p-4"
                  >
                    <p className={`mb-1 text-xs font-semibold ${item.color}`}>
                      {item.role}
                    </p>
                    <p className="text-xs text-white/80">{item.message}</p>
                  </div>
                ))}
              </div>

              {/* ACTIVITY */}
              <div className="flex flex-wrap gap-3 pt-2 text-[0.6rem] text-white/60">
                {/* ACTIVITY */}
                {/* ACTIVITY */}
                <div className="flex flex-wrap items-center gap-x-6 gap-y-3 pt-3 text-[0.65rem] text-white/70">
                  <span className="flex items-center gap-2">
                    <Users size={14} className="text-emerald-400" />
                    3,000+ professionals active
                  </span>

                  <span className="flex items-center gap-2">
                    <MessageSquare size={14} className="text-sky-400" />
                    70,000+ verified discussions
                  </span>

                  <span className="flex items-center gap-2">
                    <ShieldCheck size={14} className="text-yellow-400" />
                    1,500+ brokers joined
                  </span>

                  <span className="flex items-center gap-2">
                    <LineChart size={14} className="text-pink-400" />
                    800+ market analysts active
                  </span>

                  <span className="flex items-center gap-2 font-medium text-white/90">
                    <Sparkles size={14} className="text-blue-400" />
                    Join the community today
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
